import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, Cell
} from 'recharts';
import { DollarSign, Calendar, User, MapPin, Phone, Mail } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import DocumentsSection from '../components/DocumentsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [scores, setScores] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, clientsRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/stats`),
          axios.get(`${API_URL}/api/clients`)
        ]);
        setStats(statsRes.data);
        if (clientsRes.data.length > 0) {
          const clientData = clientsRes.data[0];
          setClient(clientData);
          const scoresRes = await axios.get(`${API_URL}/api/credit-scores/${clientData.id}`);
          setScores(scoresRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const chartData = scores.map((s) => ({
    date: s.recorded_date ? format(new Date(s.recorded_date), 'MMM d') :
          s.updated_at ? format(new Date(s.updated_at), 'MMM d') : 'N/A',
    Experian: s.experian,
    Equifax: s.equifax,
    TransUnion: s.transunion
  }));

  const latestScore = scores.length > 0 ? scores[scores.length - 1] : null;

  const bureauColors = {
    Experian: '#1e40af',
    Equifax: '#059669',
    TransUnion: '#d97706'
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Logo + Header */}
        <div className="flex flex-col items-center text-center pt-2">
          <img
            src="/supreme-logo.png"
            alt="Supreme Credit Inc."
            className="h-32 w-auto mb-4"
            data-testid="supreme-logo"
          />
          <h1 className="text-2xl font-semibold text-slate-900">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
        </div>

        {/* Client Profile Card - ONLY: Name, DOB, Address, Phone, Email */}
        {client && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="client-profile-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-slate-900">My Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Name</p>
                    <p className="text-sm text-slate-900 font-medium">{client.first_name} {client.last_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Date of Birth</p>
                    <p className="text-sm text-slate-900">{client.dob ? format(new Date(client.dob + 'T00:00:00'), 'MMM d, yyyy') : 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Address</p>
                    <p className="text-sm text-slate-900">{client.address || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Phone</p>
                    <p className="text-sm text-slate-900">{client.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Email</p>
                    <p className="text-sm text-slate-900">{client.email}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Info */}
        {client && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="payment-info">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Monthly Payment</p>
                    <p className="text-xl font-bold text-slate-900">
                      {client.monthly_payment_amount ? `$${client.monthly_payment_amount.toLocaleString()}` : 'Not set'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {client.payment_plan || 'Monthly'} · {client.number_of_installments || client.installments || '-'} installments
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Next Payment Date</p>
                    <p className="text-xl font-bold text-slate-900">
                      {client.next_payment_date ? format(new Date(client.next_payment_date), 'MMM d, yyyy') : 'Not scheduled'}
                    </p>
                    <Badge className={
                      client.payment_status === 'Completed' ? 'bg-emerald-100 text-emerald-800 text-xs mt-1' :
                      client.payment_status === 'Overdue' ? 'bg-rose-100 text-rose-800 text-xs mt-1' :
                      'bg-slate-100 text-slate-700 text-xs mt-1'
                    } data-testid="payment-status-badge">
                      {client.payment_status || 'Not Started'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Combined Credit Score Chart */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="combined-score-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900">Credit Score Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                    <YAxis domain={[300, 850]} stroke="#64748b" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="Experian" stroke="#1e40af" strokeWidth={2.5} dot={{ fill: '#1e40af', r: 4 }} />
                    <Line type="monotone" dataKey="Equifax" stroke="#059669" strokeWidth={2.5} dot={{ fill: '#059669', r: 4 }} />
                    <Line type="monotone" dataKey="TransUnion" stroke="#d97706" strokeWidth={2.5} dot={{ fill: '#d97706', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No score data available yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Individual Bureau Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="individual-bureau-charts">
          {['Experian', 'Equifax', 'TransUnion'].map((bureau) => (
            <Card key={bureau} className="bg-white border-slate-200 shadow-sm" data-testid={`${bureau.toLowerCase()}-chart`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{bureau}</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: bureauColors[bureau] }}>
                      {latestScore ? latestScore[bureau.toLowerCase()] || '---' : '---'}
                    </p>
                  </div>
                  <div className="w-3 h-10 rounded-full" style={{ backgroundColor: bureauColors[bureau] }} />
                </div>
                <div className="h-32">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <XAxis dataKey="date" tick={false} axisLine={false} />
                        <YAxis domain={[300, 850]} hide />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '12px'
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey={bureau}
                          stroke={bureauColors[bureau]}
                          strokeWidth={2.5}
                          dot={{ fill: bureauColors[bureau], r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-slate-400">
                      No data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Score History Table */}
        {scores.length > 0 && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="score-history-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900">Score History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase">Experian</th>
                      <th className="text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase">Equifax</th>
                      <th className="text-center py-2 px-3 text-xs font-medium text-slate-500 uppercase">TransUnion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.slice().reverse().map((s, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-2 px-3 text-slate-600">
                          {s.recorded_date ? format(new Date(s.recorded_date), 'MMM d, yyyy') : '-'}
                        </td>
                        <td className="py-2 px-3 text-center font-medium text-blue-700">{s.experian}</td>
                        <td className="py-2 px-3 text-center font-medium text-emerald-700">{s.equifax}</td>
                        <td className="py-2 px-3 text-center font-medium text-amber-700">{s.transunion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents */}
        {client && (
          <DocumentsSection
            clientId={client.id}
            canDelete={false}
            userRole="client"
          />
        )}
      </div>
    </DashboardLayout>
  );
}
